// src/Admin/AdminReturnRequests.jsx
import React, { useState, useEffect } from "react";
import { Package, Search, X, CheckCircle, XCircle, AlertCircle } from "lucide-react";

const API_URL = "/api/orders/admin/returns/";

export default function AdminReturnRequests() {
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [error, setError] = useState("");
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [adminNote, setAdminNote] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchReturns();
  }, []);

  const fetchReturns = async () => {
    setLoading(true);
    setError("");
    setReturns([]);

    try {
      console.log("Fetching returns from:", API_URL);
      const res = await fetch(API_URL);

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }

      const data = await res.json();
      console.log("API Response:", data);

      const returnList = Array.isArray(data.results) ? data.results : [];
      setReturns(returnList);

    } catch (err) {
      console.error("Fetch failed:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (returnId, newStatus) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}${returnId}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status: newStatus,
          admin_note: adminNote || null,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Update failed");
      }

      alert(`${newStatus === "approved" ? "Approved" : "Rejected"}!`);
      setSelectedReturn(null);
      setAdminNote("");
      fetchReturns();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // FILTER: Search by order number or username
  const filteredReturns = Array.isArray(returns)
    ? returns.filter((r) => {
        const s = search.toLowerCase();
        const matchesSearch =
          r.order_number?.toLowerCase().includes(s) ||
          r.username?.toLowerCase().includes(s);
        const matchesFilter = statusFilter === "all" || r.status === statusFilter;
        return matchesSearch && matchesFilter;
      })
    : [];

  const getStatusBadge = (status) => {
    const map = {
      pending: "bg-yellow-100 text-yellow-800",
      approved: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
    };
    return `px-3 py-1 rounded-full text-xs font-medium ${map[status] || "bg-gray-100 text-gray-600"}`;
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="inline-block animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
        <p className="mt-3 text-gray-600">Loading returns...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-3" />
        <p className="text-red-600 font-medium">{error}</p>
        <button
          onClick={fetchReturns}
          className="mt-4 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-3">
          <Package className="w-8 h-8 text-blue-600" />
          Return Requests ({returns.length})
        </h2>
        <button
          onClick={fetchReturns}
          className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center gap-2"
        >
          <Package className="w-5 h-5" />
          Refresh
        </button>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by order number or username..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-5 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-700 uppercase text-xs tracking-wider">
              <tr>
                <th className="text-left p-4">ID</th>
                <th className="text-left p-4">Order</th>
                <th className="text-left p-4">User</th>
                <th className="text-left p-4">Item</th>
                <th className="text-left p-4">Reason</th>
                <th className="text-left p-4">Status</th>
                <th className="text-left p-4">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredReturns.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50 transition">
                  <td className="p-4 font-mono text-xs text-blue-600">
                    R{r.id.toString().padStart(3, "0")}
                  </td>
                  <td className="p-4 font-medium">{r.order_number}</td>
                  <td className="p-4">{r.username}</td>
                  <td className="p-4">
                    {r.product_name} ({r.size}/{r.color})
                  </td>
                  <td className="p-4 capitalize">{r.reason.replace("_", " ")}</td>
                  <td className="p-4">
                    <span className={getStatusBadge(r.status)}>{r.status}</span>
                  </td>
                  <td className="p-4">
                    <button
                      onClick={() => setSelectedReturn(r)}
                      className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredReturns.length === 0 && (
          <div className="text-center py-16 text-gray-500">
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg">No return requests found</p>
          </div>
        )}
      </div>

      {/* DETAIL MODAL */}
      {selectedReturn && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-2xl font-bold">
                Return #R{selectedReturn.id.toString().padStart(3, "0")}
              </h3>
              <button
                onClick={() => {
                  setSelectedReturn(null);
                  setAdminNote("");
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-7 h-7" />
              </button>
            </div>

            <div className="p-6 space-y-5 text-gray-700">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <strong>Order:</strong>{" "}
                  <span className="font-mono">{selectedReturn.order_number}</span>
                </div>
                <div>
                  <strong>User:</strong> {selectedReturn.username}
                </div>
                <div>
                  <strong>Item:</strong> {selectedReturn.product_name}
                </div>
                <div>
                  <strong>Size/Color:</strong> {selectedReturn.size} / {selectedReturn.color}
                </div>
                <div>
                  <strong>Request:</strong>{" "}
                  <span className="capitalize">{selectedReturn.action_requested}</span>
                </div>
                <div>
                  <strong>Created:</strong>{" "}
                  {new Date(selectedReturn.created_at).toLocaleString()}
                </div>
              </div>

              <div>
                <strong>Reason:</strong>
                <p className="mt-1 bg-gray-100 p-3 rounded-lg">
                  {selectedReturn.reason.replace("_", " ")}
                </p>
              </div>

              {selectedReturn.details && (
                <div>
                  <strong>Customer Note:</strong>
                  <p className="mt-1 bg-gray-100 p-3 rounded-lg italic">
                    {selectedReturn.details}
                  </p>
                </div>
              )}

              {selectedReturn.admin_note && selectedReturn.status !== "pending" && (
                <div>
                  <strong>Admin Note:</strong>
                  <p className="mt-1 bg-blue-50 text-blue-800 p-3 rounded-lg">
                    {selectedReturn.admin_note}
                  </p>
                </div>
              )}

              {selectedReturn.status === "pending" && (
                <div className="space-y-4 mt-6">
                  <div>
                    <label className="block font-medium mb-2">Admin Note (optional)</label>
                    <textarea
                      value={adminNote}
                      onChange={(e) => setAdminNote(e.target.value)}
                      rows="3"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      placeholder="Add internal note..."
                    />
                  </div>

                  <div className="flex gap-4">
                    <button
                      onClick={() => handleStatusUpdate(selectedReturn.id, "approved")}
                      disabled={actionLoading}
                      className="flex-1 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-60 flex items-center justify-center gap-2 font-medium"
                    >
                      <CheckCircle className="w-5 h-5" />
                      {actionLoading ? "Processing..." : "Approve Return"}
                    </button>
                    <button
                      onClick={() => handleStatusUpdate(selectedReturn.id, "rejected")}
                      disabled={actionLoading}
                      className="flex-1 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60 flex items-center justify-center gap-2 font-medium"
                    >
                      <XCircle className="w-5 h-5" />
                      {actionLoading ? "Processing..." : "Reject Return"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}